import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, FileText, CheckCircle2, XCircle, Loader2, RefreshCw, Trash2, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  queued: { icon: Loader2, color: "text-muted-foreground", label: "Queued" },
  parsing: { icon: Loader2, color: "text-[hsl(var(--teal))]", label: "Parsing résumé", spin: true },
  matching: { icon: Loader2, color: "text-[hsl(var(--teal))]", label: "Matching to positions", spin: true },
  completed: { icon: CheckCircle2, color: "text-emerald-600", label: "Completed" },
  failed: { icon: XCircle, color: "text-red-600", label: "Failed" },
};

export default function Upload() {
  const [jobs, setJobs] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [highAccuracy, setHighAccuracy] = useState(() => localStorage.getItem("tag_high_accuracy") === "true");
  useEffect(() => { localStorage.setItem("tag_high_accuracy", highAccuracy ? "true" : "false"); }, [highAccuracy]);

  const load = () => api.get("/processing-jobs").then((r) => {
    setJobs(r.data);
    setSelected((prev) => prev.filter((id) => r.data.some((j) => j.job_id === id)));
  }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const allSelected = jobs.length > 0 && selected.length === jobs.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : jobs.map((j) => j.job_id));

  const bulkDelete = async () => {
    const ids = [...selected];
    setBulkConfirm(false);
    setJobs((prev) => prev.filter((j) => !ids.includes(j.job_id)));
    setSelected([]);
    try {
      const { data } = await api.post("/processing-jobs/bulk-delete", { job_ids: ids });
      toast.success(`${data.deleted} résumé(s) deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
      load();
    }
  };

  const doUpload = async (files) => {
    const valid = [...files].filter((f) => /\.(pdf|docx|zip)$/i.test(f.name));
    if (!valid.length) { toast.error("Only PDF, DOCX or ZIP files are supported"); return; }
    const fd = new FormData();
    valid.forEach((f) => fd.append("files", f));
    fd.append("high_accuracy", highAccuracy ? "true" : "false");
    setUploading(true);
    try {
      const { data } = await api.post("/candidates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.queued} résumé(s) queued for AI processing`);
      load();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const rematch = async () => { await api.post("/rematch"); toast.success("Re-matching all candidates against active positions"); };

  const deleteJob = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.job_id;
    setPendingDelete(null);
    setJobs((prev) => prev.filter((j) => j.job_id !== id));
    try {
      await api.delete(`/processing-jobs/${id}`);
      toast.success("Résumé deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      load();
    }
  };

  const retryJob = async (jobId) => {
    try {
      await api.post(`/processing-jobs/${jobId}/retry`);
      setJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: "parsing", error: "" } : j)));
      toast.success("Retrying résumé processing");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
      load();
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between mb-6 fade-up">
        <div>
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--teal))] font-semibold">Ingestion</div>
          <h1 className="font-head text-3xl font-semibold tracking-tight mt-1">Upload Résumés</h1>
          <p className="text-muted-foreground mt-2">Bulk upload PDF, DOCX or a ZIP archive. Each résumé is parsed and matched automatically.</p>
        </div>
        <Button variant="outline" onClick={rematch} data-testid="rematch-btn"><RefreshCw size={15} className="mr-1.5" /> Re-run all matches</Button>
      </div>

      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
        onClick={() => inputRef.current.click()}
        className={`border-2 border-dashed rounded-2xl p-14 text-center cursor-pointer transition-colors ${dragging ? "border-[hsl(var(--teal))] bg-[hsl(var(--teal))]/5" : "border-border bg-white hover:border-[hsl(var(--teal))]/50"}`}>
        <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.zip" className="hidden" data-testid="file-input"
          onChange={(e) => doUpload(e.target.files)} />
        <UploadCloud size={44} className="mx-auto text-[hsl(var(--teal))]" />
        <p className="font-head font-bold text-lg mt-4">{uploading ? "Uploading…" : "Drag & drop résumés here"}</p>
        <p className="text-sm text-muted-foreground mt-1">or click to browse · PDF, DOCX, ZIP · multiple files supported</p>
      </div>

      <div className="mt-3 flex items-center justify-between bg-white border border-border rounded-xl px-4 py-3" data-testid="high-accuracy-row">
        <div className="flex items-start gap-2.5">
          <Sparkles size={16} className="text-[hsl(var(--gold))] mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium">Higher-accuracy parsing</div>
            <div className="text-xs text-muted-foreground">Uses the premium AI model for deeper parsing on tricky résumés. Slower and costs a bit more per résumé.</div>
          </div>
        </div>
        <Switch checked={highAccuracy} onCheckedChange={setHighAccuracy} data-testid="high-accuracy-toggle" />
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg">Processing status</h3>
          {jobs.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" data-testid="select-all-jobs" checked={allSelected} onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-border accent-[hsl(var(--teal))] cursor-pointer" />
                Select all
              </label>
              {selected.length > 0 && (
                <Button size="sm" variant="outline" data-testid="bulk-delete-btn" onClick={() => setBulkConfirm(true)}
                  className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 size={14} /> Delete selected ({selected.length})
                </Button>
              )}
            </div>
          )}
        </div>
        {jobs.length > 0 && (() => {
          const total = jobs.length;
          const done = jobs.filter((j) => j.status === "completed" || j.status === "failed").length;
          const active = total - done;
          const failed = jobs.filter((j) => j.status === "failed").length;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <div data-testid="upload-progress" className="mb-4 bg-white border border-border rounded-xl px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {active > 0
                    ? <><Loader2 size={15} className="text-[hsl(var(--teal))] animate-spin" /> Processing… <span data-testid="progress-count">{done} of {total}</span> processed</>
                    : <><CheckCircle2 size={15} className="text-emerald-600" /> All {total} processed</>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {pct}%{failed > 0 && <span className="text-red-600"> · {failed} failed</span>}
                </div>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(var(--teal))] rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })()}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No processing jobs yet.</p>
        ) : (
          <div className="space-y-2">
            {jobs.map((j) => {
              const meta = STATUS_META[j.status] || STATUS_META.queued;
              const Icon = meta.icon;
              return (
                <div key={j.job_id} data-testid={`job-${j.job_id}`} className={`flex items-center gap-3 bg-white border rounded-lg px-4 py-3 transition-colors ${selected.includes(j.job_id) ? "border-[hsl(var(--teal))] bg-[hsl(var(--teal))]/5" : "border-border"}`}>
                  <input type="checkbox" data-testid={`select-job-${j.job_id}`} checked={selected.includes(j.job_id)} onChange={() => toggleSelect(j.job_id)}
                    className="h-4 w-4 rounded border-border accent-[hsl(var(--teal))] cursor-pointer shrink-0" />
                  <FileText size={18} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{j.candidate_name || j.filename}</div>
                    {j.error && <div className="text-xs text-red-600 truncate">{j.error}</div>}
                  </div>
                  <Badge variant="outline" className="gap-1.5"><Icon size={13} className={`${meta.color} ${meta.spin ? "animate-spin" : ""}`} /> {meta.label}</Badge>
                  {j.status === "failed" && (
                    <Button size="sm" variant="outline" data-testid={`retry-job-${j.job_id}`} onClick={() => retryJob(j.job_id)} className="gap-1.5 shrink-0">
                      <RefreshCw size={13} /> Retry
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`delete-job-${j.job_id}`} onClick={() => setPendingDelete(j)} className="shrink-0 text-muted-foreground hover:text-red-600">
                    <Trash2 size={15} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this résumé?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-medium text-foreground">{pendingDelete?.candidate_name || pendingDelete?.filename}</span> — including the candidate record and all of their match scores. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-job-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-job-confirm" onClick={deleteJob} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} résumé{selected.length !== 1 && "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected résumés — including their candidate records and all match scores. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-delete-confirm" onClick={bulkDelete} className="bg-red-600 hover:bg-red-700">Delete selected</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
